/**
 * IndexedDB-backed cache for raw API payloads (avatars / pentas / followings / followers).
 *
 * Values are stored as JSON strings so that the snapshot is taken synchronously at
 * `cacheWrite()` time: the API payloads we cache are mutated in place right after
 * (parseAvatar rewrites `design` and attaches a Horoscope instance), so a deferred
 * structured clone would persist parsed — and non-cloneable — data.
 */

import { API_URL } from '../apiUrl';

const DB_NAME = 'hd_cafe';
const STORE_NAME = 'api_cache';
const DB_VERSION = 1;

const SCHEMA_VERSION = 1;
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

const isSupported = () => typeof indexedDB !== 'undefined' && indexedDB !== null;

let dbPromise = null;

const openDB = () => {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (!isSupported()) {
      reject(new Error('IndexedDB is not available'));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error('IndexedDB open blocked'));
  });
  dbPromise = dbPromise.catch((error) => {
    dbPromise = null;
    throw error;
  });
  return dbPromise;
};

const withStore = (mode, action) =>
  openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, mode);
        const request = action(tx.objectStore(STORE_NAME));
        tx.onabort = () => reject(tx.error);
        tx.onerror = () => reject(tx.error);
        tx.oncomplete = () => resolve(request ? request.result : undefined);
      })
  );

const runWhenIdle = (fn) => {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(fn, { timeout: 2000 });
  } else {
    setTimeout(fn, 0);
  }
};

/**
 * Reads a cached payload. Resolves with `null` when there is nothing usable:
 * missing entry, different schema version, different API host or expired entry.
 */
export const cacheRead = (key) => {
  if (!key) return Promise.resolve(null);
  return withStore('readonly', (store) => store.get(key))
    .then((envelope) => {
      if (!envelope || envelope.v !== SCHEMA_VERSION) return null;
      if (envelope.apiUrl !== API_URL) return null;
      if (!envelope.ts || Date.now() - envelope.ts > MAX_AGE_MS) return null;
      return JSON.parse(envelope.json);
    })
    .catch(() => null);
};

/**
 * Writes are queued until the browser is idle, so a delete issued in the meantime
 * has to be able to cancel them — otherwise a stale snapshot could land after it.
 */
const pendingWrites = new Map();

/** Serializes `data` immediately, then persists it once the browser is idle. */
export const cacheWrite = (key, data) => {
  if (!key || !isSupported() || typeof data === 'undefined') return;
  let envelope;
  try {
    envelope = {
      v: SCHEMA_VERSION,
      apiUrl: API_URL,
      ts: Date.now(),
      json: JSON.stringify(data),
    };
  } catch (error) {
    return;
  }
  pendingWrites.set(key, envelope);
  runWhenIdle(() => {
    if (pendingWrites.get(key) !== envelope) return;
    pendingWrites.delete(key);
    withStore('readwrite', (store) => store.put(envelope, key)).catch(() => {});
  });
};

export const cacheDelete = (key) => {
  if (!key) return Promise.resolve();
  pendingWrites.delete(key);
  return withStore('readwrite', (store) => store.delete(key)).catch(() => {});
};

export const cacheClear = () => {
  pendingWrites.clear();
  return withStore('readwrite', (store) => store.clear()).catch(() => {});
};
